import React, { useEffect, useState } from 'react';

import * as yup from 'yup';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  CircularProgress,
  Grid,
  IconButton,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';

import { IIPENNotaryProfile } from '../../../types/Notary';
import { IVirtualizedOffice } from '../../../types/Office';
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks';
import {
  loadMoreNotariesAsync,
  searchNotariesAsync,
} from '../../redux-store/ipenNotary/ipenNotarySlice';
import { RootState } from '../../redux-store/store';
import { ISearchParams } from '../../type';
import {
  normalizePhoneInput,
  searchFilteredNotaries,
} from '../../utils/helpers';
import { ThirdPartyObject } from '../../utils/inviteNotaryHelper';
import { validationSchema } from '../../utils/validationSchemas';

export interface INotarySelector {
  changeValue: (arg: any) => void;
  initialLimit: number;
  required?: boolean;
  isLoading?: boolean;
  setSelectedNewNotary: React.Dispatch<
    React.SetStateAction<IIPENNotaryProfile | null>
  >;
  setNotaryToInvite: React.Dispatch<React.SetStateAction<ThirdPartyObject>>;
  selectedBranch?: any;
  setSelectedBranch: React.Dispatch<
    React.SetStateAction<IVirtualizedOffice | null>
  >;
  fieldsLayout?: 'row' | 'column';
}

const filter = createFilterOptions<any>();

function ConformXNotarySelector(props: INotarySelector) {
  const {
    changeValue,
    initialLimit,
    isLoading,
    required,
    setSelectedNewNotary,
    setNotaryToInvite,
    selectedBranch,
    setSelectedBranch,
    fieldsLayout,
  } = props;

  const dispatch = useAppDispatch();

  const order: any = useAppSelector((state: RootState) => state.orders.order);
  const notaries: any = useAppSelector(
    (state: RootState) => state.ipenNotary
  ).notaries;
  const notariesPaginationToken: any = notaries.paginationToken;

  const noNotaryText =
    notaries?.length > 0 ? 'Loading...' : 'No notaries found';
  const initialRegisteredNotary = order?.OrderDetails?.IPEN?.Notary
    ? order?.OrderDetails?.IPEN?.Notary?.FirstName +
      ' ' +
      order?.OrderDetails?.IPEN?.Notary?.LastName
    : '';

  const [currentLimit] = React.useState(initialLimit);
  const [value, setValue] = React.useState<any | null>(null);
  const [valueToShow, setValueToShow] = React.useState<any | null>(
    initialRegisteredNotary || null
  );
  const [searchValue, setSearchValue] = useState<any>(initialRegisteredNotary);
  const [page, setPage] = useState(0);
  const [emailAdded, setEmailAdded] = useState<boolean>(false);
  const [onlyFirstNameAdded, setOnlyFirstNameAdded] = useState<boolean>(false);
  const [notaryEnteredFirstName, setNotaryEnteredFirstName] =
    useState<string>('');
  const [notaryEnteredLastName, setNotaryEnteredLastName] =
    useState<string>('');
  const [notaryEnteredEmail, setNotaryEnteredEmail] = useState<string>('');
  const [showAdditionalFields, setShowAdditionalFields] =
    useState<boolean>(false);
  const [formErrors, setFormErrors] = useState({
    FirstName: '',
    LastName: '',
    Email: '',
    PhoneNumber: '',
  });
  const [phoneNumber, setPhoneNumber] = useState({ value: '', error: '' });

  const loadNotaries = ({
    parameters,
  }: {
    parameters: { query: string; direction?: string };
  }) => {
    dispatch(loadMoreNotariesAsync({ parameters }));
  };

  const searchNotaries = ({
    parameters,
    loadMore,
  }: {
    parameters: { query: string; direction?: string };
    loadMore: boolean;
  }) => {
    dispatch(searchNotariesAsync({ parameters, loadMore }));
  };

  const emptyNotary: ThirdPartyObject = {
    Email: {
      Value: '',
      Error: '',
    },
    FirstName: {
      Value: '',
      Error: '',
    },
    LastName: {
      Value: '',
      Error: '',
    },
    PhoneNumber: {
      Value: '',
      Error: '',
    },
  };

  useEffect(() => {
    if (selectedBranch) {
      setValueToShow('');
      setNotaryToInvite(emptyNotary);
      setSelectedNewNotary(null);
    }
  }, [selectedBranch]);

  useEffect(() => {
    changeValue(value);
  }, [value]);

  useEffect(() => {
    if (showAdditionalFields) {
      setSelectedNewNotary(null);
    } else {
      setNotaryToInvite(emptyNotary);
    }
  }, [showAdditionalFields]);

  const calculateThreeDaysAfter = (signingDate: Date) => {
    const baseDate = signingDate ? new Date(signingDate) : new Date();
    baseDate.setDate(baseDate.getDate() + 3);
    return baseDate;
  };

  const loadMoreNotaries = (newLimit: number) => {
    if (notaries) {
      const targetPage = notariesPaginationToken ? page + 1 : 0;
      const valString =
        selectedBranch && selectedBranch.Name ? selectedBranch.Name : '';
      const searchVal = searchValue ? searchValue : '';
      const threeDaysAfterSigningDate = calculateThreeDaysAfter(
        order?.OrderDetails?.SigningDate
      );
      const searchParams: any = searchFilteredNotaries(
        newLimit,
        'Active,Pending',
        threeDaysAfterSigningDate,
        true,
        valString,
        searchVal,
        targetPage,
        notariesPaginationToken
      );
      setPage(targetPage);
      if (searchParams && notariesPaginationToken) {
        loadNotaries({ parameters: searchParams });
      }
    }
  };

  const getResultsNotary = (
    branch: IVirtualizedOffice | null,
    notaryInputValue: string | null
  ) => {
    const valString = branch && branch.Name ? branch.Name : '';
    const notaryValue = notaryInputValue || '';
    const threeDaysAfterSigningDate = calculateThreeDaysAfter(
      order?.OrderDetails?.SigningDate
    );
    const searchParams: ISearchParams | undefined = searchFilteredNotaries(
      currentLimit,
      'Active,Pending',
      threeDaysAfterSigningDate,
      true,
      valString,
      notaryValue,
      0,
      notariesPaginationToken
    );
    setSelectedBranch(branch);
    if (searchParams != null) {
      searchNotaries({ parameters: searchParams, loadMore: false });
    }
  };

  const onNotaryChangeToShow = (val: any | null) => {
    getResultsNotary(selectedBranch, val);
  };

  const changeValueToShow = (value: any) => {
    onNotaryChangeToShow(value);
    setSearchValue(value);
  };

  const loadMore = () => {
    if (notariesPaginationToken) loadMoreNotaries(currentLimit + 10);
  };

  const getByPlaceholderText = () => {
    if (notaries.length === 0 && !isLoading) {
      return 'No notary found for this branch. Type a name or email to add a notary';
    } else return 'Search for or add a new notary';
  };

  const handleFieldChange = (
    fieldName: 'FirstName' | 'LastName' | 'Email' | 'PhoneNumber',
    value: string
  ): void => {
    let error = '';
    if (fieldName === 'PhoneNumber') {
      value = normalizePhoneInput(value).trimStart();
    }
    try {
      validationSchema.validateSyncAt(fieldName, {
        [fieldName]: value,
      });
      setNotaryToInvite((prev) => ({
        ...prev,
        [fieldName]: { Value: value, Error: error },
      }));
      setFormErrors((prev) => ({ ...prev, [fieldName]: '' }));
    } catch (err) {
      const validationError = err as yup.ValidationError;
      error = validationError.message;
      setNotaryToInvite((prev) => ({
        ...prev,
        [fieldName]: { Value: value, Error: error },
      }));
      setFormErrors((prev) => ({
        ...prev,
        [fieldName]: error,
      }));
    }
    if (fieldName === 'PhoneNumber') {
      setPhoneNumber({ value: value, error: error });
    }
  };

  const handleNotaryEnteredName = (val: any | null) => {
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    const isValidEmail = emailRegex.test(val);

    if (isValidEmail) {
      handleNewEmailEntered(val);
    } else {
      handleNewNameEntered(val);
    }
  };

  const handleNewEmailEntered = (val: any) => {
    setNotaryToInvite((prev) => ({
      ...prev,
      Email: {
        Value: val,
        Error: '',
      },
    }));
    setNotaryEnteredEmail(val);
    setEmailAdded(true);
    setOnlyFirstNameAdded(false);
  };

  const handleNewNameEntered = (val: any) => {
    const hasSpace = /\s/.test(val);
    if (!hasSpace) {
      setNotaryEnteredFirstName(val);
      setOnlyFirstNameAdded(true);
      setEmailAdded(false);
      setNotaryToInvite((prev) => ({
        ...prev,
        FirstName: { Value: val, Error: prev?.FirstName?.Error ?? '' },
      }));
    } else {
      const [firstName, ...rest] = val.split(/\s+/);
      const lastName = rest.join(' ');
      setNotaryEnteredFirstName(firstName);
      setNotaryEnteredLastName(lastName);
      setOnlyFirstNameAdded(false);
      setEmailAdded(false);
      setNotaryToInvite((prev) => ({
        ...prev,
        FirstName: { Value: firstName, Error: prev?.FirstName?.Error ?? '' },
        LastName: { Value: lastName, Error: prev?.LastName?.Error ?? '' },
      }));
    }
  };

  const handleResetForm = () => {
    setShowAdditionalFields(false);
    setNotaryEnteredEmail('');
    setNotaryEnteredFirstName('');
    setNotaryEnteredLastName('');
    setSelectedNewNotary(null);
    setEmailAdded(false);
    setOnlyFirstNameAdded(false);
    setNotaryToInvite(emptyNotary);
    setSelectedBranch(null);
    setPhoneNumber({ value: '', error: '' });
  };

  return (
    <>
      <Autocomplete
        disabled={showAdditionalFields}
        freeSolo
        autoHighlight
        autoComplete
        data-testid='ConformXNotarySelector'
        id='ConformXNotarySelector'
        ListboxProps={{
          onScroll: (event: React.SyntheticEvent) => {
            const listboxNode = event.currentTarget;
            if (
              listboxNode.scrollTop + listboxNode.clientHeight ===
              listboxNode.scrollHeight
            ) {
              loadMore();
            }
          },
        }}
        value={valueToShow}
        isOptionEqualToValue={(option, value) => option?.Id === value?.Id}
        onInputChange={(event, value) => {
          changeValueToShow(value);
          setValueToShow(value);
        }}
        onChange={(event, newValue) => {
          setValue(newValue);
          if (newValue && newValue.inputValue) {
            setShowAdditionalFields(true);
          }
          if (newValue && newValue.inputValue) {
            handleNotaryEnteredName(newValue.inputValue);
          }
        }}
        noOptionsText={noNotaryText}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const { inputValue } = params;
          // Suggest the creation of a new value
          const isExisting = options.some(
            (option) =>
              inputValue.toLocaleLowerCase() ===
                option.FullName?.toLocaleLowerCase() ||
              inputValue.toLocaleLowerCase() ===
                option.Email?.toLocaleLowerCase()
          );
          if (inputValue !== '' && !isExisting) {
            filtered.push({
              inputValue,
              FullName: `Add "${inputValue}"`,
            });
          }
          return filtered;
        }}
        selectOnFocus
        handleHomeEndKeys
        options={notaries}
        getOptionLabel={(option) => {
          let label: string;
          // Value selected with enter, right from the input
          if (typeof option === 'string') {
            label = option;
          } else if (option.inputValue) {
            // Add 'xxx' option created dynamically
            label = option.inputValue;
          } else {
            // Add combined name and email field to allow search to filter correctly when results are returned.
            label = `${option.FirstName} ${option.LastName} (${option.Email})`;
          }
          return label;
        }}
        renderOption={(props, option) => {
          if (option.Email) {
            return (
              <ListItem {...props} key={option.Id} data-testid={option.Id}>
                <ListItemText
                  primary={
                    <>
                      {`${option.FullName} `}
                      <Typography
                        sx={{ display: 'inline' }}
                        component='span'
                        variant='body1'
                        color='text.secondary'>
                        ({option.Email})
                      </Typography>
                    </>
                  }
                  secondary={option.Branch ? `${option.Branch}` : null}
                />
              </ListItem>
            );
          } else {
            return (
              <li
                key={option.Id}
                data-testid={'notarySelectorAddOption'}
                {...props}>
                {option.FullName}
              </li>
            );
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            data-testid={'notarySelectorSearch'}
            variant='standard'
            label={`Notary${required ? ' *' : ''}`}
            placeholder={getByPlaceholderText()}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? (
                    <CircularProgress color='inherit' size={20} />
                  ) : null}
                  {showAdditionalFields && (
                    <IconButton
                      type='button'
                      aria-label='remove notary'
                      onClick={() => {
                        handleResetForm();
                        setValue(null);
                      }}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  )}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
      {showAdditionalFields && (
        <Grid container mb={2} mt={2} wrap='nowrap'>
          <Grid item flexGrow={1}>
            <Grid container spacing={2} direction={fieldsLayout}>
              <Grid item xs={3}>
                <TextField
                  id='firstName'
                  label='First Name'
                  variant='standard'
                  required
                  fullWidth
                  autoFocus={emailAdded}
                  onChange={(e) =>
                    handleFieldChange('FirstName', e.target.value)
                  }
                  defaultValue={notaryEnteredFirstName}
                  error={!!formErrors.FirstName}
                  helperText={formErrors.FirstName}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id='lastName'
                  label='Last Name'
                  variant='standard'
                  required
                  fullWidth
                  autoFocus={onlyFirstNameAdded}
                  onChange={(e) =>
                    handleFieldChange('LastName', e.target.value)
                  }
                  defaultValue={notaryEnteredLastName}
                  error={!!formErrors.LastName}
                  helperText={formErrors.LastName}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id='email'
                  label='Email'
                  variant='standard'
                  required
                  autoFocus={!emailAdded && !onlyFirstNameAdded}
                  placeholder='username@domain.com'
                  fullWidth
                  onChange={(e) => handleFieldChange('Email', e.target.value)}
                  defaultValue={notaryEnteredEmail}
                  error={!!formErrors.Email}
                  helperText={formErrors.Email}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id='phone'
                  label='Phone Number'
                  variant='standard'
                  required
                  fullWidth
                  value={phoneNumber.value}
                  onChange={(e) =>
                    handleFieldChange('PhoneNumber', e.target.value)
                  }
                  error={!!formErrors.PhoneNumber}
                  helperText={formErrors.PhoneNumber}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
}

export default ConformXNotarySelector;
